import axios from 'axios';
import { extractFromCookies } from '../helpers/utils';
import api from './api';

import type { CheckAccountDAC } from '../interface/dac.interface';
import { APIProxy } from './api-proxy';

const API_GATEWAY_URL_CUSTOMER = process.env.API_GATEWAY_URL_CUSTOMER;

const getAuthData = async (context, isUseGatewayAPI?) => {
  const accessToken = extractFromCookies('access-token', context?.req?.headers?.cookie);
  const refreshToken = extractFromCookies('refresh-token', context?.req?.headers?.cookie);
  try {
    if (accessToken && typeof accessToken !== 'undefined') {
      let userDataResponse = null;
      let usedAccessToken = accessToken;
      let isRefreshTokenExpired = false;
      let newAccessToken;
      let newRefreshToken;
      do {
        try {
          const response = await api.get(
            'customer/v1/saphybris/customers/current?deviceType=web',
            isUseGatewayAPI
              ? {
                  baseURL: API_GATEWAY_URL_CUSTOMER,
                  headers: {
                    Authorization: `Bearer ${usedAccessToken}`,
                  },
                }
              : {
                  headers: {
                    Authorization: `Bearer ${usedAccessToken}`,
                  },
                },
            false,
            false,
            false
          );
          userDataResponse = response?.data;
        } catch (err) {
          console.log('Failed retrieve user data', err);
          if (err.response?.data?.errors?.find((data) => data?.type === 'InvalidTokenError')) {
            try {
              const refreshTokenResponse = await api.post(
                'customer/v1/saphybris/refresh-token',
                null,
                {
                  refresh_token: refreshToken,
                },
                isUseGatewayAPI
                  ? {
                      baseURL: API_GATEWAY_URL_CUSTOMER,
                    }
                  : {},
                false,
                false
              );
              newAccessToken = refreshTokenResponse?.data?.access_token;
              newRefreshToken = refreshTokenResponse?.data?.refresh_token;
              if (newAccessToken && newRefreshToken) {
                usedAccessToken = newAccessToken;
                context.res.setHeader('Set-Cookie', [
                  `access-token=${newAccessToken}; HttpOnly; Path=/`,
                  `refresh-token=${newRefreshToken}; HttpOnly; Path=/`,
                ]);
              } else {
                isRefreshTokenExpired = true;
              }
            } catch (errR) {
              console.error('Error fetching auth data:', err);
              isRefreshTokenExpired = true;
              userDataResponse = errR?.response?.data || errR?.message;
            }
          } else {
            isRefreshTokenExpired = true;
            console.log('User is unauthenticated: ', err);
          }
        }
      } while (!isRefreshTokenExpired && userDataResponse === null);

      if (isRefreshTokenExpired) {
        context.res.setHeader('Set-Cookie', [
          `access-token=${newAccessToken}; HttpOnly; Path=/;`,
          `refresh-token=${newRefreshToken}; HttpOnly; Path=/;`,
        ]);
      }
      return userDataResponse;
    } else {
      console.log('User unauthenticated');
      throw new Error(null);
    }
  } catch (err) {
    console.log('Failed retrieve user data', err);
    return null;
  }
};

// this version will be run on server side of nextjs API basepath/api/user-authentication
// and return any error response from hybris API to give the information
const getAuthDataNew = async (context, fields?, returnToken?: boolean) => {
  const accessToken = extractFromCookies('access-token', context?.req?.headers?.cookie);
  const refreshToken = extractFromCookies('refresh-token', context?.req?.headers?.cookie);
  const fieldsType = context?.req?.query?.fields || fields;
  const customerPseudoIDCookie = extractFromCookies('_ga', context?.req?.headers?.cookie);
  const customerPseudoId = customerPseudoIDCookie?.replace(/^GA\d+\.\d+\./, '');
  try {
    if ((accessToken && typeof accessToken !== 'undefined') || refreshToken) {
      let userDataResponse = null;
      let usedAccessToken = accessToken;
      let isRefreshTokenExpired = false;
      let newAccessToken;
      let newRefreshToken;
      do {
        try {
          const response = await axios.get('customer/v1/saphybris/customers/current', {
            baseURL: process.env.NEXT_PUBLIC_BASE_URL,
            params: {
              deviceType: 'web',
              fields: fieldsType,
              customerPseudoId,
            },
            headers: {
              Authorization: `Bearer ${usedAccessToken}`,
            },
          });
          userDataResponse = response?.data;
        } catch (err) {
          if (refreshToken) {
            try {
              const refreshTokenResponse = await api.post(
                'customer/v1/saphybris/refresh-token',
                null,
                {
                  refresh_token: refreshToken,
                  clientId: process.env.HYBRIS_CLIENT_ID,
                  clientSecret: process.env.HYBRIS_CLIENT_SECRET,
                },
                {},
                false,
                false
              );
              if (
                refreshTokenResponse?.data?.access_token &&
                refreshTokenResponse?.data?.refresh_token
              ) {
                newAccessToken = refreshTokenResponse?.data?.access_token;
                newRefreshToken = refreshTokenResponse?.data?.refresh_token;
                usedAccessToken = newAccessToken;
                context.res.setHeader('Set-Cookie', [
                  `access-token=${newAccessToken}; HttpOnly; Path=/;`,
                  `refresh-token=${newRefreshToken}; HttpOnly; Path=/;`,
                ]);
              } else {
                isRefreshTokenExpired = true;
              }
            } catch (errR) {
              isRefreshTokenExpired = true;
            }
          } else {
            isRefreshTokenExpired = true;
          }
        }
      } while (!isRefreshTokenExpired && userDataResponse === null);

      if (isRefreshTokenExpired) {
        context.res.setHeader('Set-Cookie', [
          `access-token=${newAccessToken}; HttpOnly; Path=/;${!newAccessToken || typeof newAccessToken === 'undefined' ? `Max-Age=0;` : ''}`,
          `refresh-token=${newRefreshToken}; HttpOnly; Path=/;${
            !newRefreshToken || typeof newRefreshToken === 'undefined' ? `Max-Age=0;` : ''
          }`,
        ]);
      }
      return returnToken
        ? { ...(userDataResponse || {}), accessToken: usedAccessToken }
        : userDataResponse;
    } else {
      throw new Error(null);
    }
  } catch (err) {
    return null;
  }
};

const getUserAuth = async (fieldsType?: 'BASIC') => {
  try {
    const response = await api.get(
      '/api/user-authentication',
      { baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL, params: { fields: fieldsType } },
      false,
      false,
      false
    );
    return response;
  } catch (err) {
    console.log(err);
    return err;
  }
};

const getSystemAuth = async (isExpired = false) => {
  try {
    const response = await api.get(
      isExpired ? '/api/system-authentication?isExpired=true' : '/api/system-authentication',
      { baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL },
      false,
      false,
      false
    );
    return response;
  } catch (err) {
    console.log(err);
    return err;
  }
};

const getUserDACAuth = async (payload) => {
  try {
    const responseSFAuth = await api.post(
      'customer/v1/saphybris/customers/sf-token',
      null,
      payload
    );

    await api.post('/api/dac-authentication', null, responseSFAuth?.data, {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    });

    return responseSFAuth;
  } catch (err) {
    return err;
  }
};

const setUserAuthCookie = (context, customCookiesList: string[] = []) => {
  if (!context?.req?.body?.access_token) {
    throw new Error('No access token provided');
  }

  const accessToken = context?.req?.body?.access_token;
  const expires = new Date(Date.now() + context?.req?.body?.expires_in * 1000).toUTCString();

  if (context?.req?.body?.refresh_token) {
    const refreshToken = context?.req?.body?.refresh_token;
    context.res.setHeader('Set-Cookie', [
      `access-token=${accessToken}; HttpOnly; Path=/; expires=${expires}`,
      `refresh-token=${refreshToken}; HttpOnly; Path=/`,
      ...customCookiesList,
    ]);
  } else {
    context.res.setHeader('Set-Cookie', [
      `access-token=${accessToken}; HttpOnly; Path=/; expires=${expires}`,
      ...customCookiesList,
    ]);
  }
};

const setUserAuthCookieTemp = (context) => {
  if (!context?.req?.body?.access_token) {
    throw new Error('No access token provided');
  }

  const accessToken = context?.req?.body?.access_token;
  const expires = new Date(Date.now() + context?.req?.body?.expires_in * 1000).toUTCString();

  const authCookie = [`access-token=${accessToken}; HttpOnly; Path=/; expires=${expires}`];

  if (context?.req?.body?.refresh_token) {
    authCookie.push(`refresh-token=${context?.req?.body?.refresh_token}; HttpOnly; Path=/;`);
  }

  const temp = btoa(JSON.stringify(authCookie));

  context.res.setHeader('Set-Cookie', [
    `temp=${temp}; HttpOnly; Path=/;`,
    `temp-access-token=${accessToken}; HttpOnly; Path=/; expires=${expires}`,
  ]);
};

const setUserAuthCookieForAccountChoosing = (context, customCookiesList: string[] = []) => {
  if (!context?.req?.body?.access_token) {
    throw new Error('No access token provided');
  }

  const accessToken = context?.req?.body?.access_token;
  const expires = new Date(Date.now() + context?.req?.body?.expires_in * 1000).toUTCString();

  if (context?.req?.body?.refresh_token) {
    const refreshToken = context?.req?.body?.refresh_token;
    context.res.setHeader('Set-Cookie', [
      `access-token-account-choosing=${accessToken}; HttpOnly; Path=/; expires=${expires}`,
      `refresh-token-account-choosing=${refreshToken}; HttpOnly; Path=/`,
      ...customCookiesList,
    ]);
  } else {
    context.res.setHeader('Set-Cookie', [
      `access-token-account-choosing=${accessToken}; HttpOnly; Path=/; expires=${expires}`,
      ...customCookiesList,
    ]);
  }
};

const setUserAuthCookieFromTemp = (context) => {
  const temp = extractFromCookies('temp', context?.req?.headers?.cookie);

  if (!temp) {
    throw new Error('No temp cookie provided');
  }

  const authCookie = JSON.parse(atob(temp));

  context.res.setHeader('Set-Cookie', authCookie);
};

const signInWithSocialMedia = async (payload) => {
  try {
    const res = await api.post('customer/v1/saphybris/customer/login-social-media', null, {
      ...payload,
      clientId: process.env.HYBRIS_CLIENT_ID,
      clientSecret: process.env.HYBRIS_CLIENT_SECRET,
      deviceType: 'web',
      needVerification: true,
    });

    if (res.status === 200 && res.data.acknowledge === 1) {
      // Set token to cookie
      await api.post('/api/user-login', null, res?.data, {
        baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
      });

      return res;
    } else {
      return res;
    }
  } catch (err) {
    const error =
      err?.response?.data?.errors ??
      err?.response?.data?.error ??
      err?.message ??
      'User is not authenticated';

    return {
      error,
    };
  }
};

const userCheckAccount = (payload) =>
  api.post('customer/v1/saphybris/customers/check-account', null, {
    ...payload,
    needVerification: true,
  });

const userRegister = (payload) =>
  api.post('customer/v1/saphybris/register', null, {
    ...payload,
    deviceType: 'web',
    needVerification: true,
  });

const userLogin = (payload) =>
  api.post('customer/v1/saphybris/user-auto-token', null, {
    ...payload,
    clientId: process.env.HYBRIS_CLIENT_ID,
    clientSecret: process.env.HYBRIS_CLIENT_SECRET,
    deviceType: 'web',
    needVerification: true,
  });

const clientUserRegister = (payload) =>
  api.post('/api/register', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

const clientUserLogin = (payload) =>
  api.post('/api/login', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

const clientUserLoginEmail = (payload, isSendPseudoId = true) =>
  api.post('/api/login-email', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    params: {
      isSendPseudoId,
    },
  });

const clientUserLoginPhone = (payload, isSendPseudoId = true) =>
  api.post('/api/login-phone', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    params: {
      isSendPseudoId,
    },
  });

const forgotPassword = async (userId) => {
  return api.get(`customer/v1/saphybris/customer/forgot-password?userId=${userId}`);
};

const clientForgotPassword = async (payload) => {
  return api.post('/api/forgot-password', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const resetPassword = async (payload) => {
  return api.post('customer/v1/saphybris/customers/reset-password', null, payload);
};

const clientResetPassword = async (payload) => {
  return api.post('/api/reset-password', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const checkPhonenumberVerified = async (context) => {
  const accessToken = extractFromCookies('temp-access-token', context?.req?.headers?.cookie);
  return api.post(
    'customer/v1/saphybris/check-phonenumber-verified-on-other-account',
    null,
    context.req?.body,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

const clientCheckPhonenumberVerified = async (payload) => {
  return api.post('/api/check-phonenumber-verified', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const updateProfile = async (context) => {
  const accessToken = extractFromCookies('temp-access-token', context?.req?.headers?.cookie);
  if (context?.req?.body?.otpType === 'whatsapp') {
    return api.post(
      'customer/v1/saphybris/customers/update-profile?otpType=whatsapp',
      null,
      { ...context.req?.body, isWAVerified: true, needVerification: true },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  }
  return api.post(
    'customer/v1/saphybris/customers/update-profile?otpType=sms',
    null,
    { ...context.req?.body, isPhoneVerified: true, needVerification: true },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

const clientUpdateProfile = async (payload) => {
  return api.post('/api/profile', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const emailActivationProfile = async (context) => {
  const accessToken = extractFromCookies('access-token', context?.req?.headers?.cookie);
  return api.get(
    `customer/v1/saphybris/customer/email-activation?email=${context.req?.body?.email}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
    false,
    false,
    false
  );
};

const clientEmailActivationProfile = async (payload) => {
  return api.post('/api/email-activation-profile', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
};

const validateRestPasswordToken = async (token: string) =>
  api.get(`customer/v1/saphybris/customer/validate-forgot-password?token=${token}`);

const authRefreshToken = async (token = null) => {
  return axios.get('api/get-refresh-token', {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    ...(token && {
      params: {
        token: token,
      },
    }),
  });
};

const checkAccountDAC = (
  uniqueID: string,
  transactionType: string,
  verification: boolean,
  req?,
  res?
): Promise<{ data: CheckAccountDAC }> => {
  if (req && res) {
    const apiProxy = new APIProxy(req, res);
    return apiProxy.makeRequest({
      method: req.method,
      url: `saphybris-proxy/v1/saphybris/digital-account-opening/check-account-dac/${uniqueID}?verification=${verification}&transactionType=${transactionType}`,
    });
  } else {
    return api.get(
      `saphybris-proxy/v1/saphybris/digital-account-opening/check-account-dac/${uniqueID}?verification=${verification}&transactionType=${transactionType}`
    );
  }
};

const loginDAC = (
  payload: {
    phoneNumber: string;
    customerTDMSNo: string;
    uniqueID: string;
    transactionType: string;
  },
  req?,
  res?
) => {
  if (req && res) {
    const apiProxy = new APIProxy(req, res);
    return apiProxy.makeRequest({
      method: req.method,
      url: 'saphybris-proxy/v1/saphybris/digital-account-opening/token-customer-dac',
      data: { ...payload, needVerification: true },
    });
  } else {
    return api.post(
      'saphybris-proxy/v1/saphybris/digital-account-opening/token-customer-dac',
      null,
      { ...payload, needVerification: true }
    );
  }
};

const registerDAC = (
  payload: {
    customerName: string;
    customerTDMSNo: string;
    uniqueID: string;
    mobilePhone: string;
    email: string;
    verificationCategory: string;
    astraID: string;
    referralEmployeeNo: string;
    needVerification?: boolean;
  },
  req?,
  res?
) => {
  if (req && res) {
    const customerPseudoIDCookie = extractFromCookies('_ga', req?.headers?.cookie);
    const customerPseudoId = customerPseudoIDCookie?.replace(/^GA\d+\.\d+\./, '');
    const apiProxy = new APIProxy(req, res);
    return apiProxy.makeRequest({
      method: req.method,
      url: 'saphybris-proxy/v1/saphybris/digital-account-opening/register-dac',
      data: { ...payload, customerPseudoId, deviceType: 'web' },
    });
  } else {
    return api.post('saphybris-proxy/v1/saphybris/digital-account-opening/register-dac', null, {
      ...payload,
    });
  }
};

const changePhoneNumberDAC = (
  payload: {
    mobilePhoneNew: string;
    mobilePhoneOld: string;
    verificationCategory: string;
    uniqueID: string;
  },
  req?,
  res?
) => {
  if (req && res) {
    const customerPseudoIDCookie = extractFromCookies('_ga', req?.headers?.cookie);
    const customerPseudoId = customerPseudoIDCookie?.replace(/^GA\d+\.\d+\./, '');
    const apiProxy = new APIProxy(req, res);
    return apiProxy.makeRequest({
      method: req.method,
      url: 'saphybris-proxy/v1/saphybris/digital-account-opening/change-phone-dac',
      data: { ...payload, needVerification: true, customerPseudoId, deviceType: 'web' },
    });
  } else {
    return api.post('saphybris-proxy/v1/saphybris/digital-account-opening/change-phone-dac', null, {
      ...payload,
      needVerification: true,
    });
  }
};

const loginDACClient = (payload: {
  phoneNumber: string;
  customerTDMSNo: string;
  uniqueID: string;
  transactionType: string;
  nama: string;
}) =>
  api.post('/api/login-dac', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

const registerDACClient = (payload: {
  customerName?: string;
  customerTDMSNo?: string;
  uniqueID: string;
  mobilePhone: string;
  email?: string;
  verificationCategory: string;
  astraID?: string;
  referralEmployeeNo?: string;
  otp?: string;
  needOtp?: boolean;
  subscription?: boolean;
  googleToken?: string;
  facebookToken?: string;
  needVerification?: boolean;
}) =>
  api.post('/api/register-dac', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

const changePhoneNumberDACClient = (payload: {
  mobilePhoneNew: string;
  mobilePhoneOld: string;
  verificationCategory: string;
  uniqueID: string;
  customerTDMSNo?: string;
  otp?: string;
  needOtp?: boolean;
}) =>
  api.post('/api/change-phone-number-dac', null, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

const getFacebookUser = async (accessToken: string) => {
  try {
    const response = await axios.get(`https://graph.facebook.com/me`, {
      params: {
        access_token: accessToken,
        fields: 'email, name',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch Facebook user');
  }
};

const loginFacebookClient = ({ userId, facebookToken }) =>
  api.post(
    '/api/login-facebook',
    null,
    {
      userId,
      facebookToken,
    },
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

export default {
  getAuthData,
  getUserAuth,
  getAuthDataNew,
  getUserDACAuth,
  setUserAuthCookie,
  setUserAuthCookieTemp,
  signInWithSocialMedia,
  userLogin,
  userCheckAccount,
  userRegister,
  clientUserLogin,
  clientUserRegister,
  clientUserLoginEmail,
  clientUserLoginPhone,
  forgotPassword,
  clientForgotPassword,
  resetPassword,
  clientResetPassword,
  checkPhonenumberVerified,
  clientCheckPhonenumberVerified,
  updateProfile,
  clientUpdateProfile,
  setUserAuthCookieFromTemp,
  emailActivationProfile,
  clientEmailActivationProfile,
  validateRestPasswordToken,
  authRefreshToken,
  checkAccountDAC,
  loginDAC,
  registerDAC,
  changePhoneNumberDAC,
  loginDACClient,
  registerDACClient,
  changePhoneNumberDACClient,
  getSystemAuth,
  setUserAuthCookieForAccountChoosing,
  getFacebookUser,
  loginFacebookClient,
};
